import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ListaCarregada, PacienteCpf, ParamRelatorio, RelatorioAgenda } from '../../../../interfaces/relatorios-agenda.dto';
import { RelatoriosAgendaService } from '../../../../services/relatorios/relatorios-agenda.service';
import { ListaSelecaoComponent } from '../../../shared/lista-selecao/lista-selecao.component';
import {
  OpcoesCampo, Valores, caminhoDaFonte, campoDoCpf, ehSelecao, escolhaAjustada, faltaObrigatorio, montarCorpo,
  opcoesDoCampo, sugestoesDoDatalist, urlDaFonte, valorDoCheckbox, valorSincronizado, valoresIniciais, visivel,
} from '../../../../utils/relatorio-filtros.util';

type Previa = { estado: 'buscando' | 'achou' | 'nao-achou' | 'erro'; texto: string };

const PLACEHOLDER_POR_TIPO: Record<string, string> = {
  text: 'Digite...', number: 'Digite um número...', date: 'Selecione uma data...', 'datetime-local': 'Selecione data e hora...',
};

@Component({
  selector: 'app-relatorio-filtros',
  standalone: true,
  imports: [CommonModule, FormsModule, ListaSelecaoComponent],
  templateUrl: './relatorio-filtros.component.html',
})
export class RelatorioFiltrosComponent implements OnInit {
  @Input({ required: true }) relatorio!: RelatorioAgenda;
  /** Valores a pré-preencher (ex.: últimos relatórios); chaves que não são parâmetro são ignoradas. */
  @Input() valoresIniciais?: Record<string, string>;
  @Input() gerando = false;
  @Input() mensagem = '';
  @Input() tipoMensagem: 'erro' | 'aviso' | 'info' = 'erro';
  @Output() gerar = new EventEmitter<Record<string, string>>();
  @Output() fechar = new EventEmitter<void>();

  valores: Valores = {};
  opcoes: Record<string, OpcoesCampo> = {};
  todos: Record<string, boolean> = {};
  marcados: Record<string, Set<string>> = {};
  previas: Record<string, Previa> = {};
  erroValidacao = '';

  // Cache por URL enquanto o modal está aberto, como o dataCache da Agenda.
  private listas = new Map<string, ListaCarregada>();
  private emVoo = new Set<string>();
  private urlDoCheckbox = new Map<string, string | null>();

  constructor(private servico: RelatoriosAgendaService) {}

  get params(): ParamRelatorio[] {
    return this.relatorio?.params ?? [];
  }

  ngOnInit(): void {
    this.valores = valoresIniciais(this.params);
    for (const p of this.params) {
      const v = this.valoresIniciais?.[p.name];
      if (v !== undefined && v !== null) this.valores[p.name] = String(v);
    }
    for (const p of this.params.filter(x => x.type === 'checkbox-list')) {
      const marcados = new Set((this.valores[p.name] ?? '').split(',').filter(Boolean));
      this.marcados[p.name] = marcados;
      this.todos[p.name] = marcados.size === 0;
    }
    this.atualizar();
    for (const p of this.params.filter(x => this.temPreviaDeCpf(x) && (this.valores[x.name] ?? '').trim())) {
      this.aoSairDoCpf(p);
    }
  }

  ehSelecao = ehSelecao;
  visivel(p: ParamRelatorio): boolean { return visivel(p, this.valores); }
  temPreviaDeCpf(p: ParamRelatorio): boolean { return p.type === 'cpf-lookup' || (p.type === 'text' && /cpf/i.test(p.name)); }
  placeholder(p: ParamRelatorio): string { return p.placeholder || (p.type === 'cpf-lookup' ? 'Digite o CPF...' : PLACEHOLDER_POR_TIPO[p.type] ?? ''); }
  sugestoes(p: ParamRelatorio): string[] { return sugestoesDoDatalist(p, this.listaDe(p)); }
  tipoDoInput(p: ParamRelatorio): string { return p.type === 'cpf-lookup' ? 'text' : (p.type || 'text'); }

  alterar(p: ParamRelatorio, valor: string): void {
    this.valores[p.name] = valor ?? '';
    const s = valorSincronizado(p, this.valores[p.name], this.listaDe(p));
    if (s) this.valores[s.campo] = s.valor;
    this.erroValidacao = '';
    this.atualizar();
  }

  alternarTodos(p: ParamRelatorio, marcado: boolean): void {
    this.todos[p.name] = marcado;
    if (marcado) this.marcados[p.name].clear();
    this.valores[p.name] = valorDoCheckbox(this.todos[p.name], [...this.marcados[p.name]]);
    this.atualizar();
  }

  alternarItem(p: ParamRelatorio, valor: string, marcado: boolean): void {
    if (marcado) {
      this.marcados[p.name].add(valor);
      this.todos[p.name] = false;
    } else {
      this.marcados[p.name].delete(valor);
    }
    this.valores[p.name] = valorDoCheckbox(this.todos[p.name], [...this.marcados[p.name]]);
    this.atualizar();
  }

  async aoSairDoCpf(p: ParamRelatorio): Promise<void> {
    const cpf = (this.valores[p.name] ?? '').trim();
    const oculto = campoDoCpf(p);
    if (!cpf) {
      delete this.previas[p.name];
      if (oculto) this.valores[oculto] = '';
      return;
    }
    this.previas[p.name] = { estado: 'buscando', texto: 'Buscando paciente...' };
    try {
      const r = await this.servico.dados<PacienteCpf>(`data/patient-by-cpf?cpf=${encodeURIComponent(cpf)}`);
      if ((this.valores[p.name] ?? '').trim() !== cpf) return; // resposta de uma busca já superada
      if (r?.found) {
        this.previas[p.name] = { estado: 'achou', texto: `Paciente: ${r.NAME ?? ''}` };
        if (oculto) this.valores[oculto] = String(r.PATIENT_ID ?? '');
      } else {
        this.previas[p.name] = { estado: 'nao-achou', texto: 'Paciente não encontrado com este CPF' };
        if (oculto) this.valores[oculto] = '';
      }
    } catch {
      if ((this.valores[p.name] ?? '').trim() !== cpf) return;
      this.previas[p.name] = { estado: 'erro', texto: 'Erro ao buscar paciente' };
      if (oculto) this.valores[oculto] = '';
    }
  }

  aoTeclarEnter(e: KeyboardEvent, alvo: EventTarget | null): void {
    const el = alvo as HTMLElement | null;
    if (el?.closest?.('app-lista-selecao') || el?.tagName === 'BUTTON' || el?.tagName === 'TEXTAREA') return;
    e.preventDefault();
    this.enviar();
  }

  enviar(): void {
    if (this.gerando) return;
    if (faltaObrigatorio(this.params, this.valores)) {
      this.erroValidacao = 'Por favor, preencha todos os campos obrigatórios.';
      return;
    }
    this.erroValidacao = '';
    this.gerar.emit(montarCorpo(this.params, this.valores, p => this.listaDe(p)));
  }

  private listaDe(p: ParamRelatorio): ListaCarregada | undefined {
    const url = urlDaFonte(p, this.params, this.valores);
    return url ? this.listas.get(url) : undefined;
  }

  /** Recalcula opções e escolhas; busca as URLs novas. Repete até estabilizar (escolha automática destrava dependentes). */
  private atualizar(): void {
    for (let volta = 0; volta < this.params.length + 1; volta++) {
      let mudou = false;
      for (const p of this.params) {
        const url = urlDaFonte(p, this.params, this.valores);
        if (url && !this.listas.has(url)) this.buscar(url);
        if (p.type === 'checkbox-list') {
          // Como o app.js: trocou a fonte, a lista volta com "Todos" marcado.
          // De null para URL é a dependência resolvendo, não troca: mantém a seleção semeada.
          const anterior = this.urlDoCheckbox.get(p.name) ?? null;
          if (anterior !== null && anterior !== url) {
            this.todos[p.name] = true;
            this.marcados[p.name].clear();
            this.valores[p.name] = '';
          }
          this.urlDoCheckbox.set(p.name, url);
        }
        if (!ehSelecao(p) && p.type !== 'checkbox-list') continue;
        const lista = url ? this.listas.get(url) : undefined;
        const ops = opcoesDoCampo(p, url, lista, this.valores);
        this.opcoes[p.name] = ops;
        if (ehSelecao(p)) {
          const nova = escolhaAjustada(p, this.valores[p.name] ?? '', ops, lista);
          if (nova !== (this.valores[p.name] ?? '')) {
            this.valores[p.name] = nova;
            mudou = true;
          }
        }
      }
      if (!mudou) return;
    }
  }

  private async buscar(url: string): Promise<void> {
    if (this.emVoo.has(url)) return;
    this.emVoo.add(url);
    try {
      const dados = await this.servico.dados<any[]>(caminhoDaFonte(url));
      this.listas.set(url, { itens: Array.isArray(dados) ? dados : [], falhou: false });
    } catch {
      // Marca a falha: lista vazia sozinha parece "não há ninguém".
      this.listas.set(url, { itens: [], falhou: true });
    } finally {
      this.emVoo.delete(url);
      this.atualizar();
    }
  }
}
